import React from 'react';
import { useParams } from 'react-router-dom';
import { Slide } from 'react-slideshow-image';
import 'react-slideshow-image/dist/styles.css';
import './ProjectDetail.css';


interface ProjectData {
  name: string;
  phase?: string;
  description: string;
  sizeRange: string;
  stats: {
    towers: number;
    levels: string;
    apartments: number;
    offices?: number;
    commercial?: number;
  };
  heroImage: string;
  interiorImages: string[];
  color: string;
  backgroundColor: string;
}

const ProjectDetail: React.FC = () => {
  const { projectName } = useParams<{ projectName: string }>();

  const projectsData: Record<string, ProjectData> = {
    antares: {
      name: 'ANTARES',
      phase: 'FASE II',
      description: 'Apartamentos de 1 a 3 habitaciones',
      sizeRange: 'Desde 38 m² → Hasta 69 m²',
      stats: {
        towers: 7,
        levels: '4-5',
        apartments: 200,
        offices: 18
      },
      heroImage: '/img/antares/vista-antares-aerea.jpg',
      interiorImages: [
        '/img/antares/loby-antares-1.jpg',
        '/img/antares/loby-antares-2.jpg',
        '/img/antares/loby-antares-3.jpg'
      ],
      color: '#98281F',
      backgroundColor: '#ffffff'
    },
    nogales: {
      name: 'NOGALES',
      phase: 'FASE III',
      description: 'Apartamentos de 2 a 3 habitaciones',
      sizeRange: 'Desde 126 m² → Hasta 245 m²',
      stats: {
        towers: 4,
        levels: '5 - 7',
        apartments: 63,
        commercial: 31,
        offices: 12
      },
      heroImage: '/img/nogales/vista-nogales-aerea.png',
      interiorImages: [
        '/img/nogales/loby-nogales-1.jpg',
        '/img/nogales/loby-nogales-2.jpg',
        '/img/nogales/loby-nogales-3.jpg',
        '/img/nogales/loby-nogales-4.jpg'
      ],
      color: '#717A00',
      backgroundColor: '#ffffff'
    },
    eonia: {
      name: 'EONIA',
      description: 'Apartamentos de 3 a 4 habitaciones',
      sizeRange: 'Desde 194 m² → Hasta 329 m²',
      stats: {
        towers: 3,
        levels: '4 - 12',
        apartments: 39,
        commercial: 9,
        offices: 7
      },
      heroImage: '/img/eonia/vista-eonia-aerea.jpg',
      interiorImages: [
        '/img/eonia/loby-eonia-1.jpg',
        '/img/eonia/loby-eonia-2.jpg',
        '/img/eonia/loby-eonia-3.jpg'
      ],
      color: '#5C5EAA',
      backgroundColor: '#ffffff'
    },
    azalea: {
      name: 'AZALEA',
      description: 'Apartamentos de 2 a 4 habitaciones',
      sizeRange: 'Desde 146 m² → Hasta 532 m²',
      stats: {
        towers: 2,
        levels: '6 - 14',
        apartments: 40,
        commercial: 20
      },
      heroImage: '/img/azalea/vista-aerea.jpg',
      interiorImages: [
        '/img/azalea/loby-azalea-1.jpg',
        '/img/azalea/loby-azalea-2.jpg',
        '/img/azalea/loby-azalea-3.jpg',
        '/img/azalea/loby-azalea-4.jpg'
      ],
      color: '#AC3A5F',
      backgroundColor: '#ffffff'
    },
  };

  const project = projectsData[projectName || ''];

  // Count the number of stats for the current project
  const getStatsCount = (stats: any) => {
    return Object.keys(stats).length;
  };


  if (!project) {
    return <div className="project-not-found">Proyecto no encontrado</div>;
  }

  return (
    <div className="project-detail" style={{ backgroundColor: project.backgroundColor }}>
      <div className="project-hero">
        <div className="hero-content">
          
          <div className="project-info-grid">
            <div className="project-description">
              
              <div className="project-title">
                <img 
                  src={`/img/${projectName}/logo-${projectName}-title.png`}
                  alt={`${project.name} ${project.phase || ''}`}
                  className="project-title-logo"
                />
              </div>

              <p className='project-subtitle-description'>{project.description}</p>
              <p className='project-size-range'>{project.sizeRange}</p>
            </div>
          </div>
          
          <div className="project-image">
            <img 
              src={project.heroImage}
              alt={`Vista aérea de ${project.name}`}
              className="hero-image"
            />
          </div>
        </div>
      </div>

      <div className="project-stats">
          <div className={`stats-container ${getStatsCount(project.stats) === 5 ? 'five-stats' : ''}`}>
            <div className="stat-item">
              <div className="stat-number" style={{ color: project.color }}>
                {project.stats.towers}
              </div>
              <div className="stat-label">
                TORRES DE<br />USO MIXTO
              </div>
            </div>
            
            <div className="stat-item">
              <div className="stat-number" style={{ color: project.color }}>
                {project.stats.levels}
              </div>
              <div className="stat-label">
                NIVELES<br />POR TORRE
              </div>
            </div>
            
            <div className="stat-item">
              <div className="stat-number" style={{ color: project.color }}>
                {project.name === 'ANTARES' && '+'}{project.stats.apartments}
              </div>
              <div className="stat-label">
                APARTAMENTOS
              </div>
            </div>
            
            {project.stats.commercial && (
              <div className="stat-item">
                <div className="stat-number" style={{ color: project.color }}>
                  {project.stats.commercial}
                </div>
                <div className="stat-label">
                  LOCALES<br />COMERCIALES
                </div>
              </div>
            )}
            
            {project.stats.offices && (
              <div className="stat-item">
                <div className="stat-number" style={{ color: project.color }}>
                  {project.stats.offices}
                </div>
                <div className="stat-label">
                  OFICINAS
                </div>
              </div>
            )}
          </div>
        </div>

      <div className="project-gallery">
        <div className="gallery-container">
          <Slide 
            key={projectName}
            slidesToScroll={1} 
            slidesToShow={1} 
            indicators={(index) => (
              <div className="dot" key={index}></div>
            )}
            arrows={true}
            infinite={true}
          >
            {project.interiorImages.map((image, index) => (
              <div key={index} className="slide-item">
                <img 
                  src={image}
                  alt={`Vista interior de ${project.name} - ${index + 1}`}
                  className="interior-image"
                />
              </div>
            ))}
          </Slide>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
